import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ApiClient,
  type ApiError,
  type ApiResult,
  type CommandIntent,
  errorMessageFa,
  isApiError,
} from '@saay/api-client';
import {
  applyFieldErrors,
  Banner,
  Button,
  type ButtonVariant,
  Dialog,
  DialogActions,
  type DialogSize,
  DiscardChangesDialog,
  FieldErrorList,
} from '../kit.js';
import { type ApprovalPending, type CommandOutcome, useCommand } from '@saay/web-app';
import { type ReactNode, useState } from 'react';
import {
  type DefaultValues,
  type FieldValues,
  type Path,
  useForm,
  type UseFormReturn,
} from 'react-hook-form';
import type { z } from 'zod';

import { AdminCommandFeedback, useAdminFeedback } from './admin-feedback.js';
import { ADMIN_FORM_OPTIONS, mutationSubmitDisabled, useSubmitBlocked } from './form.js';

/**
 * One command form in a dialog (FTD §14.3): React Hook Form with the contract bounds in Zod, one
 * `useCommand` intent (one `Idempotency-Key`, reused by «بررسی وضعیت»), the server's `fieldErrors`
 * mapped onto the fields, and no optimistic success:
 *
 * - a `2xx` calls `onSuccess` (the screen refreshes from the server and closes);
 * - a `202 ApprovalPending` shows «برای تأیید ارسال شد» with a link to the request, and nothing else;
 * - an error shows `messageFa` and the request ID, plus the screen's recovery line for the code.
 */
export interface CommandDialogProps<Values extends FieldValues, Body, Result> {
  readonly children: (form: UseFormReturn<Values, unknown, Values>) => ReactNode;
  readonly defaultValues: DefaultValues<Values>;
  readonly description?: ReactNode;
  readonly eyebrow?: ReactNode;
  /** The form fields the server may name in `fieldErrors` (the rest are listed under the form). */
  readonly fields: readonly string[];
  /**
   * Server field paths that land on a form field of another name (for example `deltaIrr` on the
   * typed amount), so the message shows on the field the person filled in.
   */
  readonly fieldAliases?: Readonly<Record<string, Path<Values>>>;
  /** The recovery line under the server message, by error code. */
  readonly guidance?: (error: unknown) => ReactNode;
  readonly name: string;
  /** Maps a server error onto the form (specific codes), or reloads the resource. */
  readonly onError?: (
    error: ApiError,
    form: UseFormReturn<Values, unknown, Values>,
  ) => void | Promise<void>;
  readonly onApprovalPending?: (approval: ApprovalPending) => void | Promise<void>;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSuccess?: (result: ApiResult<Result>) => void | Promise<void>;
  readonly schema: z.ZodType<Values, unknown>;
  readonly send: (
    client: ApiClient,
    body: Body,
    intent: CommandIntent,
  ) => Promise<ApiResult<Result>>;
  /** The dialog width (default 32rem); `wide` when fields sit side by side. */
  readonly size?: DialogSize;
  readonly submitLabel: string;
  readonly title: ReactNode;
  /** The request body, or `null` when nothing changed (submit stays disabled until dirty). */
  readonly toBody: (values: Values) => Body | null;
  readonly variant?: ButtonVariant;
}

export function CommandDialog<Values extends FieldValues, Body, Result>({
  children,
  defaultValues,
  description,
  eyebrow,
  fieldAliases = {},
  fields,
  guidance,
  name,
  onApprovalPending,
  onError,
  onOpenChange,
  onSuccess,
  schema,
  send,
  size,
  submitLabel,
  title,
  toBody,
  variant = 'primary',
}: CommandDialogProps<Values, Body, Result>) {
  const feedback = useAdminFeedback();
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const [nothing, setNothing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const form = useForm<Values, unknown, Values>({
    defaultValues,
    resolver: zodResolver(schema as never) as never,
    ...ADMIN_FORM_OPTIONS,
  });
  const { isDirty } = form.formState;
  const command = useCommand<Body, Result>(name, send, {
    onApprovalPending: async (approval, result) => {
      feedback.approvalSubmitted(approval.approvalRequestId);
      onOpenChange(false);
      await onApprovalPending?.(approval);
      void result;
    },
    ...(onSuccess === undefined ? {} : { onSuccess }),
  });
  const formId = name;

  async function handle(outcome: CommandOutcome<Result>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    setUnmapped(
      applyFieldErrors(outcome.error, [...fields, ...Object.keys(fieldAliases)], (field, error) =>
        form.setError(fieldAliases[field] ?? (field as Path<Values>), error),
      ),
    );
    await onError?.(outcome.error, form);
  }

  function requestClose(): void {
    if (form.formState.isDirty && command.state.status !== 'approval-pending') {
      setDiscardOpen(true);
      return;
    }
    command.reset();
    onOpenChange(false);
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const line = error === undefined ? undefined : guidance?.(error);

  return (
    <>
      <Dialog
        {...(description === undefined ? {} : { description })}
        {...(eyebrow === undefined ? {} : { eyebrow })}
        footer={
          command.state.status === 'approval-pending' ? (
            <Button onClick={() => onOpenChange(false)} type="button" variant="secondary">
              بستن
            </Button>
          ) : (
            <DialogActions
              primary={
                <Button
                  disabled={offline.blocked}
                  form={formId}
                  loading={command.state.status === 'pending'}
                  type="submit"
                  variant={variant}
                >
                  {submitLabel}
                </Button>
              }
              secondary={
                <Button onClick={requestClose} type="button" variant="ghost">
                  انصراف
                </Button>
              }
            />
          )
        }
        fullScreenOnCompact
        {...(size === undefined ? {} : { size })}
        onOpenChange={(open) => {
          if (!open) {
            requestClose();
          }
        }}
        open
        stickyFooter
        title={title}
      >
        <form
          className="admin-form"
          id={formId}
          noValidate
          onSubmit={form.handleSubmit(async (values) => {
            setUnmapped([]);
            const body = toBody(values);
            if (body === null) {
              setNothing(true);
              return;
            }
            setNothing(false);
            await handle(await command.submit(body));
          })}
        >
          {children(form)}
          {nothing ? <Banner tone="info">هیچ تغییری وارد نشده است.</Banner> : null}
          <FieldErrorList errors={unmapped} />
          {/* A refusal with its own recovery line shows that line only (W4: it showed twice). */}
          {line === undefined || line === null ? (
            <AdminCommandFeedback
              onCheckStatus={() => void command.checkStatus().then(handle)}
              state={command.state}
            />
          ) : null}
          {line === undefined || line === null ? null : (
            <div className="saay-banner" data-tone="warning" role="alert">
              <div>
                <p>{errorMessageFa(error)}</p>
                <div>{line}</div>
              </div>
            </div>
          )}
        </form>
      </Dialog>
      <DiscardChangesDialog
        onConfirmDiscard={() => {
          setDiscardOpen(false);
          command.reset();
          form.reset();
          onOpenChange(false);
        }}
        onOpenChange={setDiscardOpen}
        open={discardOpen}
      />
    </>
  );
}
